import { DbBooking, DbServiceBookingStatus, FutureBooking } from "./models";
import { DbVisit, DbVisitType } from "../visit/models";
import {
  collection,
  CollectionReference,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  query,
  Timestamp,
  where,
  orderBy,
  updateDoc,
  startAfter,
} from "firebase/firestore/lite";
import { DateTime } from "luxon";
import { firebaseDb } from "../firebase/client-app";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";
import { DbBusinessPaymentType, DbBusinessReview } from "../business/models";
import { reservedBookingService } from "./reserved";
import { bookingVisitsService } from "./visits-service";

export class BookingService {
  bookingRef(firestore: Firestore): CollectionReference {
    return collection(firestore, "booking");
  }

  newBookingId(firestore: Firestore): string {
    return doc(this.bookingRef(firestore)).id;
  }

  async getBooking(
    bookingId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking | null> {
    const bookingRef = doc(this.bookingRef(firestore), bookingId);
    try {
      const bookingDoc = await getDoc(bookingRef);
      if (!bookingDoc.exists) {
        return null;
      }
      return bookingDoc.data() as DbBooking;
    } catch (_error) {
      return null;
    }
  }

  async getBookingsByIds(
    businessId: string,
    bookingIds: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    if (bookingIds.length === 0) return [];

    const chunks: string[][] = [];
    const chunkSize = 10;
    for (let i = 0; i < bookingIds.length; i += chunkSize) {
      chunks.push(bookingIds.slice(i, i + chunkSize));
    }

    const queries = chunks.map(async (chunk) => {
      const snap = await getDocs(
        query(
          bookingService.bookingRef(firestore),
          where("business_id", "==", businessId),
          where("id", "in", chunk),
        ),
      );
      return snap.docs.map((d) => d.data() as DbBooking);
    });

    const results = await Promise.all(queries);
    return results.flat();
  }

  async getUserBookings(
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    const today = DateTime.now().startOf("day").toJSDate();
    const bookingRef = query(
      this.bookingRef(firestore),
      where("user_id", "==", userId),
      where("booking_date", ">=", today),
      orderBy("booking_date", "asc"),
    );
    const bookingDocs = await getDocs(bookingRef);
    return bookingDocs.docs.map((doc) => doc.data() as DbBooking);
  }

  async getUserPastBookings(
    userId: string,
    lastFetchedDate: Date,
    pageLimit: number,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    let bookingQuery = query(
      this.bookingRef(firestore),
      where("user_id", "==", userId),
      orderBy("booking_date", "desc"),
      limit(pageLimit),
    );
    if (lastFetchedDate) {
      bookingQuery = query(bookingQuery, startAfter(lastFetchedDate));
    }
    const bookingDocs = await getDocs(bookingQuery);
    return bookingDocs.docs.map((doc) => doc.data() as DbBooking);
  }

  async createReservedBooking(
    businessId: string,
    bookings: FutureBooking[],
    clientId?: string,
  ): Promise<DbBooking> {
    const first = bookings[0];
    const res = await axiosInstance.post(
      "reserve/book",
      {
        business_id: businessId,
        service_id: first.service_id,
        entity_id: first.entity_id,
        booking_date: first.booking_date.toISO(),
        service_time: first.service_time,
        service_duration: first.service_duration * bookings.length,
        client_id: clientId,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );

    return res.data as DbBooking;
  }

  async cancelReservedBooking(
    businessId: string,
    bookingId: string,
  ): Promise<DbBooking | null> {
    try {
      const res = await axiosInstance.post(
        "reserve/cancel",
        {
          business_id: businessId,
          booking_id: bookingId,
        },
        { headers: { Authorization: getCookie("Authorization")?.toString() } },
      );
      return res.data;
    } catch (error: any) {
      if (error?.response?.status === 400) {
        return null;
      }
      throw error;
    }
  }

  async confirmReservedBooking(
    businessId: string,
    bookingId: string,
    paymentId?: string | null,
    membershipId?: string | null,
    note?: string,
    cashAmount?: number | null,
    adjustedTotalAmount?: number | null,
    payment_type?: DbBusinessPaymentType | null,
    rewardPointsType?: DbBusinessPaymentType | null,
    rewardPointsAmount?: number | null,
    rewardPoints?: number | null,
  ): Promise<DbBooking> {
    const data = {
      business_id: businessId,
      booking_id: bookingId,
      payment_id: paymentId,
      payment_membership_id: membershipId,
      additional_note: note,
      cash_amount: cashAmount,
      adjusted_total_amount: adjustedTotalAmount,
      payment_type: payment_type,
      reward_type: rewardPointsType,
      reward_amount: rewardPointsAmount,
      reward_points: rewardPoints,
    };
    const res = await axiosInstance.post("reserve/confirm", data, {
      headers: { Authorization: getCookie("Authorization")?.toString() },
    });
    const response = res.data as DbBooking;
    return response;
  }

  async getBookings(
    businessId: string,
    startDate: DateTime | null,
    endDate: DateTime | null,
    firestore: Firestore = firebaseDb,
    status?: DbServiceBookingStatus,
  ): Promise<DbBooking[]> {
    try {
      const filters = [where("business_id", "==", businessId)];

      if (startDate && endDate) {
        filters.push(
          where("booking_date", ">=", Timestamp.fromDate(startDate.toJSDate())),
          where("booking_date", "<=", Timestamp.fromDate(endDate.toJSDate())),
        );
      }
      const visitsRef = query(
        bookingVisitsService.visitRef(firestore),
        ...filters,
      );
      const visitDocs = await getDocs(visitsRef);
      const visits = visitDocs.docs.map((doc) => doc.data() as DbVisit);

      let bookings = await bookingVisitsService.getBookingsFromVisits(
        visits,
        businessId,
        firestore,
      );

      if (status) {
        bookings = bookings.filter((b) => b.booking_status === status);
      }

      return bookings;
    } catch (_error) {
      throw "Something went wrong!";
    }
  }

  async getConfirmedBookings(
    businessId: string,
    startDate: DateTime | null,
    endDate: DateTime | null,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    return this.getBookings(
      businessId,
      startDate,
      endDate,
      firestore,
      DbServiceBookingStatus.confirmed,
    );
  }

  async confirmBooking(bookingId: string): Promise<DbBooking> {
    const res = await axiosInstance.post(
      "booking/confirm",
      {
        booking_id: bookingId,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return res.data;
  }

  async cancelBooking(bookingId: string): Promise<DbBooking> {
    const res = await axiosInstance.post(
      "booking/cancel",
      {
        booking_id: bookingId,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return res.data;
  }

  async getBookingByShareId(shareId: string) {
    const response = await axiosInstance.get(`share/${shareId}`);
    return response.data;
  }

  async addReview(
    bookingId: string,
    data: DbBusinessReview,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.bookingRef(firestore), bookingId), {
      review: data,
    });
  }

  async updateBookingDuration(
    businessId: string,
    bookingId: string,
    duration: number,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.bookingRef(firestore), bookingId), {
      service_duration: duration,
    });
    await reservedBookingService.updateConsumedSlotDuration(
      businessId,
      bookingId,
      duration,
    );
  }

  async getMembershipBookings(
    businessId: string,
    userId: string,
    membershipId: string,
    lastBooking?: DbBooking,
    pageLimit: number = 10,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    let visitsQuery = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
      where("user_id", "==", userId),
      orderBy("booking_date", "desc"),
      limit(pageLimit),
    );
    if (lastBooking?.booking_date instanceof Timestamp) {
      visitsQuery = query(visitsQuery, startAfter(lastBooking.booking_date));
    }
    const visitsSnap = await getDocs(visitsQuery);
    const visits = visitsSnap.docs.map((d) => d.data() as DbVisit);

    const bookings = await bookingVisitsService.getBookingsFromVisits(
      visits,
      businessId,
      firestore,
    );
    return bookings.filter((b) => b.payment_membership_id === membershipId);
  }

  async addBookingPayment(
    businessId: string,
    bookingId: string,
    type: DbBusinessPaymentType,
    amount: number,
    paymentId: string | null = null,
  ): Promise<DbBooking> {
    const response = await axiosInstance.post(
      "/booking/payment",
      {
        business_id: businessId,
        booking_id: bookingId,
        payment_id: paymentId,
        type,
        amount,
      },
      {
        headers: {
          Authorization: getCookie("Authorization")?.toString(),
        },
      },
    );
    return response.data as DbBooking;
  }

  async undoCashPayment(
    businessId: string,
    bookingId: string,
  ): Promise<DbBooking> {
    const response = await axiosInstance.delete("/booking/payment", {
      data: {
        business_id: businessId,
        booking_id: bookingId,
      },
      headers: {
        Authorization: getCookie("Authorization")?.toString(),
      },
    });
    return response.data as DbBooking;
  }

  async adjustBookingAmount(
    bookingId: string,
    amount: number,
    membershipId: string | null,
  ): Promise<DbBooking> {
    const response = await axiosInstance.put(
      `/booking/${bookingId}`,
      {
        booking_amount: membershipId ? null : amount,
        booking_id: bookingId,
        payment_membership_id: membershipId,
        payment_membership_amount: membershipId ? amount : null,
      },
      {
        headers: {
          Authorization: getCookie("Authorization")?.toString(),
        },
      },
    );
    return response.data as DbBooking;
  }

  async getAllBookingsOfBusiness(
    businessId: string,
    pageSize: number = 10,
    startAfterDate?: DateTime | null,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    let visitsQuery = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
      where("type", "==", DbVisitType.Advance),
      orderBy("booking_date", "desc"),
      limit(pageSize),
    );
    if (startAfterDate) {
      visitsQuery = query(
        visitsQuery,
        startAfter(Timestamp.fromDate(startAfterDate.toJSDate())),
      );
    }
    const visitsDocs = await getDocs(visitsQuery);
    const visits = visitsDocs.docs.map((d) => d.data() as DbVisit);

    return bookingVisitsService.getBookingsFromVisits(
      visits,
      businessId,
      firestore,
    );
  }

  async getConfirmedBookingsFromVisits(
    businessId: string,
    startDate: DateTime | null,
    endDate: DateTime | null,
    firestore: Firestore = firebaseDb,
  ): Promise<DbVisit[]> {
    try {
      const filters = [where("business_id", "==", businessId)];

      if (startDate && endDate) {
        filters.push(
          where("booking_date", ">=", Timestamp.fromDate(startDate.toJSDate())),
          where("booking_date", "<=", Timestamp.fromDate(endDate.toJSDate())),
          where("status", "==", DbServiceBookingStatus.confirmed),
        );
      }
      const visitsRef = query(
        bookingVisitsService.visitRef(firestore),
        ...filters,
      );
      const visitDocs = await getDocs(visitsRef);
      return visitDocs.docs.map((doc) => doc.data() as DbVisit);
    } catch (_error) {
      throw "Something went wrong!";
    }
  }

  async getCancelledUserBookingCountsForBusiness(
    businessId: string,
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    const visits = await bookingVisitsService.getUserVisitsForBusiness(
      businessId,
      userId,
      null,
      null,
      firestore,
    );

    const bookingIds: string[] = [];
    visits
      .filter((visit) => visit.type === DbVisitType.Advance)
      .forEach((visit) => {
        visit.services.forEach((service) => {
          if (service.booking_id && !bookingIds.includes(service.booking_id)) {
            if (service.type === DbVisitType.Advance) {
              bookingIds.push(service.booking_id);
            }
          }
        });
      });

    if (bookingIds.length === 0) return 0;

    const bookings = await this.getBookingsByIds(
      businessId,
      bookingIds,
      firestore,
    );
    return bookings.filter(
      (booking) => booking.booking_status === DbServiceBookingStatus.cancelled,
    ).length;
  }

  async getBookingsForDateRange(
    businessId: string,
    startDate: DateTime,
    endDate: DateTime,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBooking[]> {
    const bookings = await this.getBookings(
      businessId,
      startDate,
      endDate,
      firestore,
    );
    return bookings;
  }
}

export const bookingService = new BookingService();
